import { useCallback, useMemo, useState } from "react";
import { trackEvent } from "@hopper-b2b/api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  CfarTrackingEvents,
  CfarTrackingPageSelected,
  ChfarTrackingEvents,
  ChfarTrackingPageSelected,
  DisruptionTrackingEvents,
  type FareDetails,
  type FiatPrice,
  FintechProductType,
} from "@hopper-b2b/types";
import {
  ButtonWrap,
  IconComponent,
  IconName,
  MobilePopoverCard,
  TermsAndConditions,
} from "@hopper-b2b/ui";
import { uberOpenExternalLink } from "@hopper-b2b/utilities";
import clsx from "clsx";
import AirplaneDepart from "../../../assets/client/airplane-depart.svg?react";
import Arrows from "../../../assets/client/arrows.svg?react";
import Card from "../../../assets/client/card-fintech.svg?react";
import Clock from "../../../assets/client/clock-simple-icon.svg?react";
import Headset from "../../../assets/client/headset.svg?react";
import { FINTECH_TERMS_CONDITIONS_LINK } from "../../../utils/urlPaths";
import styles from "./styles.module.scss";

export interface IFintechLearnMoreModal {
  open: boolean;
  onClose: () => void;
  productType?: FintechProductType;
  validUntilDate?: string;
  fareDetails: FareDetails;
}

export const FintechLearnMoreModal = ({
  open = false,
  onClose,
  productType,
  validUntilDate,
  fareDetails,
}: IFintechLearnMoreModal) => {
  const [openTerms, setOpenTerms] = useState(false);
  const { t, formatFiatCurrency } = useI18nContext();

  const pricing = fareDetails?.paxPricings[0].pricing;
  const totalCostFiat: FiatPrice = useMemo(() => {
    return {
      value:
        (pricing?.baseAmount?.fiat.value || 0) +
        (pricing?.taxAmount?.fiat.value || 0) +
        (pricing?.additionalMargin ? pricing.additionalMargin.fiat.value : 0),
      currencyCode: pricing?.baseAmount.fiat.currencyCode,
      currencySymbol: pricing?.baseAmount.fiat.currencySymbol,
    };
  }, [pricing]);

  const handleOpenTerms = useCallback(() => {
    setOpenTerms(true);
    switch (productType) {
      case FintechProductType.CfarPurchase:
      case FintechProductType.CfarExercise:
        trackEvent({
          eventName: CfarTrackingEvents.TAP_TERMS_CONDITIONS,
          properties: {
            page_selected: FintechProductType.CfarPurchase
              ? CfarTrackingPageSelected.OFFER
              : CfarTrackingPageSelected.EXERCISE,
          },
        });
        break;
      case FintechProductType.Disruption:
        trackEvent({
          eventName: DisruptionTrackingEvents.TAP_DELAY_TERMS,
          properties: undefined,
        });
        break;
      case FintechProductType.Chfar:
        trackEvent({
          eventName: ChfarTrackingEvents.TAP_TERMS_CONDITIONS,
          properties: {
            page_selected: ChfarTrackingPageSelected.OFFER,
          },
        });
        break;
    }
  }, [productType]);

  const handleOpenDetails = useCallback(() => {
    uberOpenExternalLink(FINTECH_TERMS_CONDITIONS_LINK);
  }, []);

  const header = useMemo(() => {
    switch (productType) {
      case FintechProductType.Disruption:
        return t("disruptionProtection.header");
      default:
        return null;
    }
  }, [productType, t]);

  const productName = useMemo(() => {
    switch (productType) {
      case FintechProductType.CfarPurchase:
        return t("cfar.header");
      case FintechProductType.Disruption:
        return t("disruptionProtection.header");
      case FintechProductType.Chfar:
        return t("chOffer.title");
      default:
        return null;
    }
  }, [productType, t]);

  const title = useMemo(() => {
    switch (productType) {
      case FintechProductType.CfarPurchase:
        return t("cfar.header");
      case FintechProductType.Disruption:
        return t("disruptionPurchase.learnMore.title");
      case FintechProductType.Chfar:
        return t("chOffer.title");
      default:
        return null;
    }
  }, [productType, t]);

  const description = useMemo(() => {
    switch (productType) {
      case FintechProductType.Disruption:
        return t("disruptionPurchase.learnMore.description");
      default:
        return null;
    }
  }, [productType, t]);

  const partialRefundAmount = useMemo(() => {
    if (!totalCostFiat?.value) return;
    const value = totalCostFiat?.value * 0.8;
    return formatFiatCurrency(
      { ...totalCostFiat, value },
      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    );
  }, [formatFiatCurrency, totalCostFiat]);

  const fullRefundAmount = useMemo(() => {
    if (!totalCostFiat?.value) return;
    return formatFiatCurrency(totalCostFiat, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }, [formatFiatCurrency, totalCostFiat]);

  const valueProps = useMemo(() => {
    switch (productType) {
      case FintechProductType.CfarPurchase:
        return [
          {
            icon: <Clock />,
            content: t("cfarOffers.learnMore.valueProps.first", {
              timeFrame: validUntilDate,
            }),
          },
          {
            icon: <Card />,
            content: t("cfarOffers.learnMore.valueProps.second", {
              partialRefundAmount,
              fullRefundAmount,
            }),
          },
          {
            icon: <Headset />,
            content: t("cfarOffers.learnMore.valueProps.third"),
          },
        ];
      case FintechProductType.Chfar:
        return [
          {
            icon: <Arrows />,
            content: t("chOffer.learnMore.valueProps.first"),
          },
          {
            icon: <Card />,
            content: t("chOffer.learnMore.valueProps.second"),
          },
          {
            icon: <Clock />,
            content: t("chOffer.learnMore.valueProps.third"),
          },
        ];
      case FintechProductType.Disruption:
        return [
          {
            icon: <AirplaneDepart />,
            content: t("disruptionPurchase.learnMore.valueProps.first"),
          },
          {
            icon: <Card />,
            content: t("disruptionPurchase.learnMore.valueProps.second"),
          },
          {
            icon: <Headset />,
            content: t("disruptionPurchase.learnMore.valueProps.third"),
          },
        ];
      default:
        return null;
    }
  }, [fullRefundAmount, partialRefundAmount, productType, t, validUntilDate]);

  const moreInfo = useMemo(() => {
    switch (productType) {
      case FintechProductType.CfarPurchase:
        return (
          <Trans
            i18nKey={"cfarOffers.learnMore.moreInfo"}
            components={[<li />]}
          />
        );
      case FintechProductType.Chfar:
        return (
          <Trans i18nKey={"chOffer.learnMore.moreInfo"} components={[<li />]} />
        );
      case FintechProductType.Disruption:
        return (
          <Trans
            i18nKey={"disruptionPurchase.learnMore.moreInfo"}
            components={[<li />]}
          />
        );
      default:
        return null;
    }
  }, [productType]);

  return (
    <>
      <MobilePopoverCard
        centered
        open={open}
        onClose={onClose}
        fullScreen={false}
        className={clsx("half-sheet", productType, styles.fintechLearnMore)}
        contentClassName={clsx("half-sheet-content-container")}
        headerElement={header}
        topLeftButton={
          <button className="half-sheet-close" onClick={onClose}>
            <IconComponent
              className="half-sheet-close-icon"
              name={IconName.Close}
            />
          </button>
        }
      >
        <div className="half-sheet-content">
          {title ? <div className="half-sheet-title">{title}</div> : null}
          {description ? (
            <div className="half-sheet-description">
              <p className="half-sheet-segment">{description}</p>
            </div>
          ) : null}
          {valueProps ? (
            <div className="fintech-learn-more-value-props">
              {valueProps.map((valueProp) => {
                return (
                  <div className="fintech-learn-more-value-prop">
                    <div className="icon">{valueProp.icon}</div>
                    <div className="content">{valueProp.content}</div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="fintech-learn-more-information">
            <div className="fintech-learn-more-info-header">
              {t("fintechLearnMore.importantInfo.title")}
            </div>
            <ul className="fintech-learn-more-info-content">{moreInfo}</ul>
          </div>
          <div className="fintech-learn-more-book-now">
            {t("fintechLearnMore.stayFlexible")}
          </div>
          <div className="fintech-learn-more-links">
            <div className="fintech-learn-more-links-details">
              <Trans
                i18nKey="fintechLearnMore.disclaimer"
                values={{ product: productName }}
                components={[
                  <ButtonWrap
                    className="fintech-learn-more-link"
                    onClick={handleOpenDetails}
                  >
                    {t("seeHere")}
                  </ButtonWrap>,
                ]}
              />
            </div>
            <div className="fintech-learn-more-links-terms">
              <Trans
                i18nKey="seeTermsAndConditions"
                components={[
                  <ButtonWrap
                    className="fintech-learn-more-link"
                    onClick={handleOpenTerms}
                  >
                    {t("termsAndConditions")}
                  </ButtonWrap>,
                ]}
              />
            </div>
          </div>
        </div>
      </MobilePopoverCard>
      {!!productType && (
        <TermsAndConditions
          productType={productType}
          open={openTerms}
          onClose={() => setOpenTerms(false)}
        />
      )}
    </>
  );
};
